import * as Yup from "yup";
import i18next from "../../i18n/i18n";

export const ORG_SIGN_UP_FIELD_LIMITS = {
  name: 30,
  email: 50,
  phone: 15,
  password: 128,
  orgName: 100,
  gstin: 15,
};

export const ORG_NAME_ALLOWED_REGEX = /^\p{L}[\p{L}\p{M} .'-]*$/u;
export const ORGANISATION_NAME_ALLOWED_REGEX =
  /^[\p{L}\p{N}][\p{L}\p{M}\p{N} .,&'()/-]*$/u;
export const ORG_PHONE_ALLOWED_REGEX = /^[0-9]{7,15}$/;
export const GSTIN_ALLOWED_REGEX = /^[A-Z0-9]{15}$/;
const PASSWORD_ALLOWED_REGEX = /^[A-Za-z0-9@$!%*?&]+$/;
const PASSWORD_REPEATING_REGEX = /(.)\1{2,}/;
const PASSWORD_SEQUENCE_REGEX =
  /(?:012|123|234|345|456|567|678|789|987|876|765|654|543|432|321|210)/;

const validationMessage = (key, options) => () =>
  i18next.t(`homeAuthPages:${key}`, options);

const nameValidation = (fieldKey, requiredKey) =>
  Yup.string()
    .trim()
    .required(validationMessage(requiredKey))
    .matches(
      ORG_NAME_ALLOWED_REGEX,
      () => i18next.t("homeAuthPages:name_invalid_characters", {
        field: i18next.t(`homeAuthPages:${fieldKey}`),
      })
    )
    .max(
      ORG_SIGN_UP_FIELD_LIMITS.name,
      () => i18next.t("homeAuthPages:name_max_length", {
        field: i18next.t(`homeAuthPages:${fieldKey}`),
        count: ORG_SIGN_UP_FIELD_LIMITS.name,
      })
    );

const emailValidation = Yup.string()
  .trim()
  .required(validationMessage("email_required"))
  .email(validationMessage("invalid_email"))
  .max(
    ORG_SIGN_UP_FIELD_LIMITS.email,
    validationMessage("email_max_length", {
      count: ORG_SIGN_UP_FIELD_LIMITS.email,
    })
  );

const passwordValidation = Yup.string()
  .required(validationMessage("password_required"))
  .max(
    ORG_SIGN_UP_FIELD_LIMITS.password,
    validationMessage("password_max_length", {
      count: ORG_SIGN_UP_FIELD_LIMITS.password,
    })
  )
  .test(
    "password-requirements",
    validationMessage("password_requirements"),
    (value = "") =>
      value.length >= 8 &&
      /[A-Z]/.test(value) &&
      /[a-z]/.test(value) &&
      /\d/.test(value) &&
      /[@$!%*?&]/.test(value) &&
      PASSWORD_ALLOWED_REGEX.test(value) &&
      !PASSWORD_REPEATING_REGEX.test(value) &&
      !PASSWORD_SEQUENCE_REGEX.test(value)
  );

// Constants for file size and image dimensions
const MIN_FILE_SIZE = 0; // 0KB
const MAX_FILE_SIZE = 5242880; // 5MB

// State Definitions
export const state1 = {
  first_name: "",
  last_name: "",
  email: "",
  country_code: "+91",
  phone_no: "",
  password: "",
  confirm_password: "",
};

export const state2 = {
  org_name: "",
  state: "",
  gstin: "",
  incorporation_certificate: null, // File input initialized to null incorporation_certificate
  gst_certificate: null, // File input initialized to null
};

// Schema Definitions
const restrictedDomains = [
  "gmail.com",
  "yahoo.com",
  "outlook.com",
  "hotmail.com",
]; // Add more domains as needed

export const schema1 = Yup.object().shape({
  first_name: nameValidation("first_name", "first_name_required"),
  last_name: nameValidation("last_name", "last_name_required"),
  email: emailValidation
    .test(
      "restricted-domain",
      validationMessage("company_email_create_org"),
      (value) => {
        if (!value) return true; // If email is empty, let Yup handle the required validation
        const domain = value.split("@")[1];
        return domain && !restrictedDomains.includes(domain);
      }
    ),
  phone_no: Yup.string()
    .nullable()
    .matches(ORG_PHONE_ALLOWED_REGEX, {
      message: validationMessage("enter_valid_phone"),
      excludeEmptyString: true,
    }),

  country_code: Yup.string().required(validationMessage("country_code_required")),
  password: passwordValidation,
  confirm_password: Yup.string()
    .required(validationMessage("confirm_password_required"))
    .oneOf([Yup.ref("password")], validationMessage("passwords_mismatch")),
});

export const schema2 = Yup.object().shape({
  org_name: Yup.string()
    .trim()
    .required(validationMessage("organisation_name_required"))
    .matches(
      ORGANISATION_NAME_ALLOWED_REGEX,
      validationMessage("organisation_name_invalid_characters")
    )
    .max(
      ORG_SIGN_UP_FIELD_LIMITS.orgName,
      validationMessage("organisation_name_max_length", {
        count: ORG_SIGN_UP_FIELD_LIMITS.orgName,
      })
    ),
  state: Yup.string().required(validationMessage("state_required")),
  gstin: Yup.string()
    .notRequired()
    .matches(GSTIN_ALLOWED_REGEX, {
      message: validationMessage("gstin_invalid"),
      excludeEmptyString: true,
    }),
  incorporation_certificate: Yup.mixed()
    .nullable()

    .test(
      "fileFormat",
      validationMessage("unsupported_file_format"),
      (value) => {
        if (!value) return true;
        return (
          value &&
          value instanceof File && // Ensure value is a File object
          ["image/png", "image/jpeg", "application/pdf"].includes(value.type)
        );
      }
    )
    .test(
      "fileSize",
      validationMessage("max_file_size", {
        size: MAX_FILE_SIZE / 1024 / 1024,
      }),
      (value) => {
        if (!value) return true;
        return (
          value && value.size >= MIN_FILE_SIZE && value.size <= MAX_FILE_SIZE
        );
      }
    ),
  gst_certificate: Yup.mixed()
    .nullable()

    .test(
      "fileFormat",
      validationMessage("unsupported_file_format"),
      (value) => {
        if (!value) return true;
        return (
          value &&
          value instanceof File && // Ensure value is a File object
          ["image/png", "image/jpeg", "application/pdf"].includes(value.type)
        );
      }
    )
    .test(
      "fileSize",
      validationMessage("max_file_size", {
        size: MAX_FILE_SIZE / 1024 / 1024,
      }),
      (value) => {
        if (!value) return true;
        return (
          value && value.size >= MIN_FILE_SIZE && value.size <= MAX_FILE_SIZE
        );
      }
    ),
});
export const STATES_IN_INDIA = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];
